import { divideArrayIntoChunks } from '@pkg/basic'
import { and, eq, inArray } from 'drizzle-orm'
import { assistantActionKeySchema } from '../../assistantSchemas'
import { sqliteMaxHostParameterCount } from '../../constants'
import {
  actionTable,
  assistantProposalActionTable,
  assistantProposalTable,
} from '../../schema'
import { orSqlConditions } from '../../sqlConditions'
import type { TransactionDatabase } from '../../types'

/**
 * 指定Actionをtargetまたはreferenceに持つ提案本体をすべて削除する。
 * 呼出側がAction変更と同じtransactionのtxを渡すことで原子的に合成できる。
 * @param params transaction内DBまたは通常DBとAction複合キー集合。
 * @returns 削除対象として見つかったproposal ID。
 */
export const deleteAssistantProposalsForActionKeys = async ({
  database,
  actionKeys,
}: {
  readonly database: TransactionDatabase
  readonly actionKeys: readonly {
    readonly sourceId: string
    readonly actionOrder: number
  }[]
}): Promise<readonly string[]> => {
  const parsedKeys = actionKeys.map((key) =>
    assistantActionKeySchema.parse(key),
  )
  if (parsedKeys.length === 0) {
    return []
  }

  const proposalIdSet = new Set<string>()
  const keyChunkSize = Math.max(1, Math.floor(sqliteMaxHostParameterCount / 2))
  for (const chunk of divideArrayIntoChunks(parsedKeys, keyChunkSize)) {
    const conditions = chunk.map((key) => {
      const condition = and(
        eq(assistantProposalActionTable.sourceId, key.sourceId),
        eq(assistantProposalActionTable.actionOrder, key.actionOrder),
      )
      if (condition === undefined) {
        throw new Error('Action key condition should not be undefined')
      }
      return condition
    })
    const rows = await database
      .select({ proposalId: assistantProposalActionTable.proposalId })
      .from(assistantProposalActionTable)
      .where(orSqlConditions({ conditions }))
    for (const row of rows) {
      proposalIdSet.add(row.proposalId)
    }
  }

  const proposalIds = [...proposalIdSet]
  for (const chunk of divideArrayIntoChunks(
    proposalIds,
    sqliteMaxHostParameterCount,
  )) {
    await database
      .delete(assistantProposalTable)
      .where(inArray(assistantProposalTable.id, [...chunk]))
  }
  return proposalIds
}

/**
 * 指定sourceに属するActionをtargetまたはreferenceに持つ提案をすべて削除する。
 * @param params transaction内DBまたは通常DBとsource ID集合。
 * @returns 削除対象として見つかったproposal ID。
 */
export const deleteAssistantProposalsForSourceIds = async ({
  database,
  sourceIds,
}: {
  readonly database: TransactionDatabase
  readonly sourceIds: readonly string[]
}): Promise<readonly string[]> => {
  if (sourceIds.length === 0) {
    return []
  }

  const proposalIdSet = new Set<string>()
  for (const chunk of divideArrayIntoChunks(
    [...new Set(sourceIds)],
    sqliteMaxHostParameterCount,
  )) {
    const rows = await database
      .select({ proposalId: assistantProposalActionTable.proposalId })
      .from(assistantProposalActionTable)
      .where(inArray(assistantProposalActionTable.sourceId, [...chunk]))
    for (const row of rows) {
      proposalIdSet.add(row.proposalId)
    }
  }
  const proposalIds = [...proposalIdSet]
  for (const chunk of divideArrayIntoChunks(
    proposalIds,
    sqliteMaxHostParameterCount,
  )) {
    await database
      .delete(assistantProposalTable)
      .where(inArray(assistantProposalTable.id, [...chunk]))
  }
  return proposalIds
}

/**
 * 指定Action IDをtargetまたはreferenceに持つ提案をすべて削除する。
 * @param params transaction内DBまたは通常DBとAction ID集合。
 * @returns 削除対象として見つかったproposal ID。
 */
export const deleteAssistantProposalsForActionIds = async ({
  database,
  actionIds,
}: {
  readonly database: TransactionDatabase
  readonly actionIds: readonly string[]
}): Promise<readonly string[]> => {
  if (actionIds.length === 0) {
    return []
  }

  const actionKeys: {
    sourceId: string
    actionOrder: number
  }[] = []
  for (const chunk of divideArrayIntoChunks(
    [...new Set(actionIds)],
    sqliteMaxHostParameterCount,
  )) {
    const rows = await database
      .select({
        sourceId: actionTable.sourceId,
        actionOrder: actionTable.order,
      })
      .from(actionTable)
      .where(inArray(actionTable.id, [...chunk]))
    actionKeys.push(...rows)
  }
  return deleteAssistantProposalsForActionKeys({ database, actionKeys })
}

/**
 * portfolio DB内のアシスタント提案を全件削除する。
 * @param params transaction内DBまたは通常DB。
 * @returns 値は返さない。
 */
export const deleteAllAssistantProposals = async ({
  database,
}: {
  readonly database: TransactionDatabase
}): Promise<void> => {
  await database.delete(assistantProposalTable)
}
